import {Component, OnDestroy, OnInit} from '@angular/core';
import {CrudPagedListComponent} from '../../_directives';
import {ActivatedRoute, Router} from '@angular/router';
import {
  AlertService,
  AudienceService,
  CampaignService,
  CanDeactivateService,
  FunnelService,
  LoaderService,
  SideNavService,
  StepEmulatorService,
  StepTemplateService,
  StorageService,
  UserService,
} from '../../_services';
import {Location} from '@angular/common';
import {
  Audience,
  Breadcrumb,
  Campaign,
  CampaignProduct,
  CustomStepCategoryEnum,
  defaultThemeColor,
  Funnel,
  FunnelStep,
  getCampaignProductImageUrl,
  getDiscountSettingFromStep,
  OfferStages,
  OfferTypeLabels,
  OfferTypes,
  Pager,
  StepCategoryLabels,
  StepEmulatorDevices,
  StepPopupActions,
  StepStaticDataImageType,
  User,
  VariantTypeEnum,
} from '../../_models';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {takeUntil} from 'rxjs/operators';
import {CanDeactivateServiceWrapper} from '../../_helpers/can-deactivate-service-wrapper';
import {
  capitalizeFirstLetter,
  createStepMediaInitialData,
  createStepResponseMediaInitialData,
  externalButtonUsage,
  getOfferBuilderRoot,
  initializeForm,
  mediaExcludedSteps,
  OfferBuilderSection,
  patchInputs,
  stepLayout,
} from '../step-util';
import {FormBuilder} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';

enum SortFilters {
  LastModified = '-updated',
  Name = 'name',
}

@Component({
  selector: 'app-step-templates',
  templateUrl: './step-templates.component.html',
  styleUrls: ['./step-templates.component.scss'],
})
export class StepTemplatesComponent extends CrudPagedListComponent implements OnInit, OnDestroy {
  offerType: OfferTypes;
  offerName: string;
  offerIntent: number;
  selectedFilter = SortFilters.LastModified;
  sortFilters = SortFilters;
  stepType: CustomStepCategoryEnum;
  stepTypeName: string;
  offerId: string;
  offer: Funnel;
  templates: any;
  showSortMenu: boolean = false;
  originalTemplates: any;
  sortAscending: boolean = true;
  breadcrumbs: Breadcrumb[] = [];
  stepPopupActions = StepPopupActions;
  popupTitle = '';
  segmentId: number;
  segmentName: string;
  popupData = {
    name: '',
    is_template: false,
    step_id: null,
    popupType: StepPopupActions.Duplicate,
  };
  searchQuery = '';
  user: User;
  campaign: Campaign;
  isGlobal: boolean = true;
  popupTitleMap = {
    [StepPopupActions.Delete]: 'Delete Template',
    [StepPopupActions.Duplicate]: 'Duplicate Template',
  };
  hoveredTemplateId: string | number = null;
  protected canDeactivateService: CanDeactivateService = CanDeactivateServiceWrapper.getObject();
  pageSize = 5;
  hoveredIcons: Record<number, any> = {};
  useExternalButtons = false;
  isAdminView: boolean = false;
  currentSection: OfferBuilderSection = OfferBuilderSection.Retention;
  private stepForms = {};
  stepDiscounts = {};
  stepCategoryLabels = StepCategoryLabels;
  activeDevice = StepEmulatorDevices.Mobile;
  theme: string = defaultThemeColor;
  popupIconMap: Partial<Record<StepPopupActions, string>> = {
    [StepPopupActions.Delete]: '/assets/stepAssets/delete-icon.svg',
    [StepPopupActions.Duplicate]: '/assets/stepAssets/duplicate.svg',
  };
  pageSeparator = '...';
  product: CampaignProduct = null;
  productPlaceholderUrl: string = '';

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    private sideNav: SideNavService,
    public ngxSmartModalService: NgxSmartModalService,
    protected loader: LoaderService,
    protected funnelService: FunnelService,
    protected campaignService: CampaignService,
    protected stepService: StepTemplateService,
    protected alertService: AlertService,
    protected userService: UserService,
    protected audienceService: AudienceService,
    protected formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private storageService: StorageService,
    protected stepEmulatorService: StepEmulatorService
  ) {
    super(router, location, route, stepService, alertService);
    this.sideNav.setShowSideNavToggle(true);
    this.sideNav.setSideNavExpanded(false);
  }

  ngOnInit() {
    this.fetchUser();
    this.campaign = this.campaignService.getCurrentCampaign();
    this.canDeactivateService.setFormDirty(false);
    this.isAdminView = this.route.snapshot.data['isAdminView'];
    this.currentSection = this.route.snapshot.data['section'];

    this.route.params.subscribe((params) => {
      this.segmentId = Number(params['segment_id']);
      this.offerId = params['offer_id'];
      this.offerType = Number(params['offer_type']);
      this.offerName = OfferTypeLabels[this.offerType];
      if (this.offerType === OfferTypes.Downsell) {
        this.offerName = getOfferBuilderRoot(this.currentSection);
      }
      this.stepType = params['step_type'];
      this.stepTypeName = StepCategoryLabels[Number(this.stepType)];
      this.generateBreadcrumbs();
    });

    if (this.offerId) {
      this.fetchFunnel();
    }

    if (this.segmentId) {
      this.fetchSegment();
    }

    const requiresVariant = this.stepType === CustomStepCategoryEnum.ChangeVariant;
    const product = this.storageService.get('selectedStepProduct');
    if (product && product.id && (!requiresVariant || product.product.variant_type !== VariantTypeEnum.None)) {
      this.product = product;
      this.updateProductImageUrl();
    } else if (this.campaign && this.campaign.id) {
      this.campaignService
        .getRandomPreviewProduct(this.campaign.id, requiresVariant)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response: any) => {
            this.product = response;
            this.updateProductImageUrl();
          },
          error: (error) => {
            this.product = null;
            this.productPlaceholderUrl = '';
            if (error.status !== 404) {
              this.handleError(error);
            }
          },
        });
    }
  }

  fetchUser() {
    this.userService
      .getCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: User) => {
          this.user = user;
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  fetchTemplates(is_global: boolean) {
    this.setFilter({ is_template: true, category: this.stepType, is_global: is_global });
    this.isGlobal = is_global;
  }

  protected onPagerData(data: Pager) {
    this.handleTemplateResponse(data);
  }

  private handleTemplateResponse(response: Pager): void {
    this.templates = response.results;
    this.processOffer();
    this.originalTemplates = this.templates;
  }

  private processOffer() {
    this.templates.forEach((step) => {
      if (step) {
        const form = initializeForm(this.formBuilder, step.category, this.offer.offer_type, false);
        if (form) {
          if (!mediaExcludedSteps.includes(step.category)) {
            form.setControl(
              'static_data',
              this.formBuilder.group({
                image: null,
                image_tablet: null,
                image_mobile: null,
                embed_code: null,
                image_type: StepStaticDataImageType.None,
              })
            );
          }
          this.stepForms[step.id] = form;
        }

        this.useExternalButtons = externalButtonUsage(step.category as CustomStepCategoryEnum);
        const stepMedia = createStepMediaInitialData();
        const stepResponseMedia = createStepResponseMediaInitialData();

        patchInputs(
          form,
          this.formBuilder,
          step,
          this.useExternalButtons,
          stepMedia,
          stepResponseMedia,
          this.sanitizer,
          this.stepEmulatorService
        );
      }
    });
  }

  getStepLayout(step: FunnelStep) {
    return stepLayout[step.category] || [];
  }

  getStepForm(step: FunnelStep) {
    return this.stepForms[step.id];
  }

  getDiscountSettingFromStep(step: FunnelStep) {
    let discountSetting = null;

    if (step) {
      if (step.id && step.id in this.stepDiscounts) {
        discountSetting = this.stepDiscounts[step.id];
      } else {
        discountSetting = getDiscountSettingFromStep(step);

        if (step.id) {
          this.stepDiscounts[step.id] = discountSetting; // cache the discount setting
        }
      }
    }

    return discountSetting;
  }

  fetchFunnel() {
    this.funnelService.get(this.offerId).subscribe(
      (response: Funnel) => {
        this.offer = response;
        this.offerIntent = response.offer_intent;
        this.ordering.push(SortFilters.LastModified);
        this.setFilter({ is_template: true, category: this.stepType, is_global: this.isGlobal });
        this.generateBreadcrumbs();
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  fetchSegment() {
    this.audienceService.get(this.segmentId).subscribe(
      (audience: Audience) => {
        this.segmentName = audience.name;
        this.generateBreadcrumbs();
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  generateBreadcrumbs() {
    const prefix = this.isAdmin ? ['admin'] : [];
    if (this.segmentId && this.segmentName) {
      this.breadcrumbs = [
        {
          label: capitalizeFirstLetter(this.offerName),
          url: [this.offerName],
        },
        {
          label: 'Segments',
          url: [this.offerName, 'stage', this.offerId, 'segment'],
        },
        {
          label: this.segmentName,
          url: [this.offerName, 'stage', this.offerId, 'segment', this.segmentId],
        },
        {
          label: 'Choose a Template',
        },
      ];
    } else {
      this.breadcrumbs = [
        {
          label: capitalizeFirstLetter(this.offerName),
          url: [...prefix, this.offerName],
        },
        {
          label: this.offer && OfferStages[this.offer.offer_intent],
          url: [...prefix, this.offerName, 'stage', this.offerId],
        },
        {
          label: 'Choose a Template',
        },
      ];
    }
  }

  selectTemplate(templateId?: string | number): void {
    const prefix = this.isAdmin ? ['admin'] : [];
    if (!templateId) {
      if (this.offerId) {
        this.router.navigate([
          ...prefix,
          this.offerName,
          'stage',
          this.offerId,
          'category',
          this.offerType,
          'step',
          this.stepType,
        ]);
      } else if (this.segmentId) {
        this.router.navigate([
          this.offerName,
          'stage',
          this.offerId,
          'segment',
          this.segmentId,
          'category',
          this.offerType,
          'step',
          this.stepType,
        ]);
      } else {
        this.router.navigate([...prefix, this.offerName, 'category', this.offerType, 'step', this.stepType]);
      }
    } else {
      if (this.offerId && !this.segmentId) {
        this.router.navigate([
          ...prefix,
          this.offerName,
          'stage',
          this.offerId,
          'category',
          this.offerType,
          'step',
          this.stepType,
          'edit',
          false,
          'template',
          templateId,
        ]);
      } else if (this.segmentId) {
        this.router.navigate([
          this.offerName,
          'stage',
          this.offerId,
          'segment',
          this.segmentId,
          'category',
          this.offerType,
          'step',
          this.stepType,
          'edit',
          false,
          'template',
          templateId,
        ]);
      } else {
        this.router.navigate([
          ...prefix,
          this.offerName,
          'category',
          this.offerType,
          'step',
          this.stepType,
          'edit',
          false,
          'template',
          templateId,
        ]);
      }
    }
  }

  createTemplate() {
    const prefix = this.isAdmin ? ['admin'] : [];
    if (this.offerId) {
      this.router.navigate([
        ...prefix,
        this.offerName,
        'stage',
        this.offerId,
        'category',
        this.offerType,
        'step',
        this.stepType,
      ]);
    } else {
      this.router.navigate([...prefix, this.offerName, 'category', this.offerType, 'step', this.stepType]);
    }
  }

  getStepPreview(e: MouseEvent, id: string | number) {
    e.stopPropagation();
    const template = this.templates.find((item) => item.id === id);
    template.previewLoading = true;

    this.stepService.get(id).subscribe((resp: any) => {
      if (resp) {
        const static_data = resp.static_data,
          response_static_data = resp.response_data.static_data || null,
          stepDetails = {
            ...resp,
            static_data: {
              ...static_data,
              image: static_data.image ? static_data.image.id : null,
              image_mobile: static_data.image_mobile ? static_data.image_mobile.id : null,
              image_tablet: static_data.image_tablet ? static_data.image_tablet.id : null,
            },
            response_data: {
              ...resp.response_data,
              ...(response_static_data && {
                static_data: {
                  ...response_static_data,
                  image: response_static_data.image ? response_static_data.image.id : null,
                  image_mobile: response_static_data.image_mobile ? response_static_data.image_mobile.id : null,
                  image_tablet: response_static_data.image_tablet ? response_static_data.image_tablet.id : null,
                },
              }),
            },
          };

        this.funnelService.previewStep(this.campaign.id, stepDetails).subscribe(
          (data) => {
            window.open(data.toString(), '_blank');
            template.previewLoading = false;
          },
          (error) => {
            this.handleError(error);
            template.previewLoading = false;
          }
        );
      }
    });
  }

  openStepPopup(e: MouseEvent, popupType: number, step: any): void {
    this.popupTitle = this.popupTitleMap[popupType] || '';

    this.popupData = {
      ...this.popupData,
      name: step.name || '',
      step_id: step.id || null,
      is_template: true,
      popupType: popupType,
    };

    this.ngxSmartModalService.getModal('stepPopup').open();
  }

  saveStep() {
    this.fetchTemplates(this.isGlobal);
    this.ngxSmartModalService.getModal('stepPopup').close();
  }

  onSortSelect(value: SortFilters): void {
    this.selectedFilter = value;
    this.ordering = [value];
    this.fetchTemplates(this.isGlobal);
  }

  toggleSortMenu() {
    this.showSortMenu = !this.showSortMenu;
  }

  get selectedSortOption() {
    return this.selectedFilter === 'name' ? 'Name' : 'Last Modified';
  }

  closeSortMenu() {
    if (this.showSortMenu) {
      this.showSortMenu = false;
    }
  }

  closeStepPopup() {
    this.ngxSmartModalService.getModal('stepPopup').close();
  }

  setHoveredTemplate(templateId: number): void {
    this.hoveredTemplateId = templateId;
    if (!this.hoveredIcons[templateId]) {
      this.hoveredIcons[templateId] = {
        select: '/assets/stepAssets/iframe-select.svg',
        edit: '/assets/stepAssets/iframe-edit.svg',
        preview: '/assets/stepAssets/iframe-preview.svg',
        clone: '/assets/stepAssets/iframe-clone.svg',
        delete: '/assets/stepAssets/iframe-delete.svg',
      };
    }
  }

  clearHoveredTemplate() {
    this.hoveredTemplateId = null;
  }

  isStepLoading(stepId: number): boolean {
    if (this.templates && this.templates.length > 0) {
      const step = this.templates.find((s) => s.id === stepId);
      return step ? step.loading : false;
    }
    return false;
  }

  editTemplate(templateId: string | number) {
    const prefix = this.isAdmin ? ['admin'] : [];
    if (this.offerId && !this.segmentId) {
      this.router.navigate([
        ...prefix,
        this.offerName,
        'stage',
        this.offerId,
        'category',
        this.offerType,
        'step',
        this.stepType,
        'edit',
        true,
        'template',
        templateId,
      ]);
    } else if (this.segmentId) {
      this.router.navigate([
        this.offerName,
        'stage',
        this.offerId,
        'segment',
        this.segmentId,
        'category',
        this.offerType,
        'step',
        this.stepType,
        'edit',
        true,
        'template',
        templateId,
      ]);
    } else {
      this.router.navigate([
        ...prefix,
        this.offerName,
        'category',
        this.offerType,
        'step',
        this.stepType,
        'edit',
        true,
        'template',
        templateId,
      ]);
    }
  }

  searchData() {
    if (this.searchQuery && this.searchQuery.trim()) {
      this.setFilter({
        is_template: true,
        category: this.stepType,
        is_global: this.isGlobal,
        name__icontains: this.searchQuery.trim(),
      });
    } else {
      this.clearSearch();
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.setFilter({
      is_template: true,
      category: this.stepType,
      is_global: this.isGlobal,
    });
  }

  navigate(url: any[]) {
    this.router.navigate(url);
  }

  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (this.pageCount <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 1; i <= this.pageCount; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const start = Math.max(2, this.page - 1);
      const end = Math.min(this.pageCount - 1, this.page + 1);

      if (start > 2) {
        pages.push(this.pageSeparator);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.pageCount - 1) {
        pages.push(this.pageSeparator);
      }

      pages.push(this.pageCount);
    }

    return pages;
  }

  updateProductImageUrl() {
    this.productPlaceholderUrl = this.product.id ? getCampaignProductImageUrl(this.product) : null;
  }

  get isAdmin(): boolean {
    return this.isAdminView && this.user && this.user.is_staff;
  }

  ngOnDestroy(): void {
    this.sideNav.setShowSideNavToggle(true);
    this.sideNav.setSideNavExpanded(true);
  }
}
